import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  FORM_ACTION_OPTIONS,
  PRODUCT_ACTION,
  PRODUCT_OPERATION_OPTIONS,
  REPEAT_ACTION,
  toggleFormAction,
} from '@/lib/formActions'

/**
 * The actions checklist, with the product operations nested under their parent.
 *
 * ONE component for all three places this list appears — the Forms list popover,
 * the form editor's settings panel and FormDefinitionPanel. They rendered the
 * same map three times, and a display rename applied by hand reached only one of
 * them; a nested parent/child list with its own toggle rules would go wrong the
 * same way three times over.
 *
 * The toggle rules themselves live in `toggleFormAction`, so this component
 * decides nothing: it renders what is checked and reports what was clicked.
 */
export function FormActionChecklist({
  value,
  onChange,
  repeatCount,
  onRepeatCountChange,
  size = 'sm',
  disabled = false,
}: {
  /** The form definition's `allowedActions`. */
  value: string[] | undefined
  /**
   * The whole next array — parent and operations move together.
   *
   * Toggling `Repeat` also passes the count it should be saved with, because
   * the action and its count are one setting: a caller that writes them in two
   * requests can fail halfway and leave a form that repeats with no number.
   * Callers with no repeat state to manage can ignore the second argument.
   */
  onChange: (next: string[], nextRepeatCount?: number | null) => void
  /**
   * The form definition's `defaultRepeatCount`. null / undefined = does not
   * repeat.
   *
   * Optional, with `onRepeatCountChange`, so a caller that has no repeat state
   * to offer simply does not render the input — the checkbox still works and
   * the count keeps whatever the server holds.
   */
  repeatCount?: number | null
  /** Called with the next count, or null when `Repeat` is switched off. */
  onRepeatCountChange?: (next: number | null) => void
  /** `xs` in the Forms list popover, `sm` in the two settings panels. */
  size?: 'xs' | 'sm'
  disabled?: boolean
}) {
  const actions = value ?? []
  const text = size === 'xs' ? 'text-xs' : 'text-sm'
  const productOn = actions.includes(PRODUCT_ACTION)
  const repeatOn = actions.includes(REPEAT_ACTION)
  const repeatEditable = onRepeatCountChange != null

  /**
   * Toggling `Repeat` has to move the count with it, in ONE update.
   *
   * Switching it off clears the count, so a stored number can never outlive the
   * action and reappear if it is switched back on — the server enforces the
   * same rule, but doing it here is what makes the input's disappearance and
   * the cleared value one act rather than two round-trips.
   *
   * Switching it on seeds 1 rather than leaving the field empty: an input with
   * no value would save as "repeats, count unknown", which is not a state the
   * backend has.
   */
  const toggleRepeat = () => {
    onChange(toggleFormAction(actions, REPEAT_ACTION), repeatOn ? null : (repeatCount ?? 1))
  }

  return (
    // `text-left` is load-bearing, not decoration. The Forms list renders this
    // inside a right-aligned actions cell (`<td class='text-right'>`), and the
    // alignment inherited straight through into every label — the checkboxes
    // stayed in their column while the words ran to the opposite edge, leaving
    // a ragged gap down the middle (z8nrz7dkgg). Stating the alignment here
    // means no future container can push these labels around either.
    <div className='space-y-0.5 text-left'>
      {FORM_ACTION_OPTIONS.map(({ value: action, label }) => (
        <div key={action} className='space-y-0.5'>
          <label
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 leading-snug transition-colors hover:bg-muted/60',
              text,
            )}
          >
            <Checkbox
              className='shrink-0'
              checked={actions.includes(action)}
              disabled={disabled}
              onCheckedChange={() =>
                action === REPEAT_ACTION && repeatEditable
                  ? toggleRepeat()
                  : onChange(toggleFormAction(actions, action))
              }
            />
            <span className='flex-1'>{label}</span>
          </label>

          {/* How many times a step running this form repeats. Shown only once
              `Repeat` is on, for the same reason the product operations are:
              a count with nothing repeating is not a setting, it is clutter. */}
          {action === REPEAT_ACTION && repeatOn && repeatEditable && (
            // Same anchored rule as the product operations, so the nesting reads
            // as "this belongs to that" rather than as a second list.
            <div className='ml-3 border-l border-border/70 pl-2.5'>
              <label className={cn('flex items-center gap-2.5 px-1.5 py-1 leading-snug', text)}>
                <span className='text-muted-foreground'>Default repeats</span>
                <Input
                  type='number'
                  min={1}
                  step={1}
                  disabled={disabled}
                  className={cn('h-7 w-20', size === 'xs' && 'h-6 text-xs')}
                  value={repeatCount ?? 1}
                  onChange={e => {
                    // An empty or half-typed box must not persist a count the
                    // backend would reject; hold the last valid one until the
                    // field parses as a whole number of at least 1.
                    const next = Number.parseInt(e.target.value, 10)
                    if (Number.isNaN(next)) return
                    onRepeatCountChange?.(Math.max(1, next))
                  }}
                />
              </label>
            </div>
          )}

          {/* The operations the "P" menu will offer. Shown only once their parent
              is on: an operation with no action to hang off cannot be reached,
              and listing them regardless would read as five more actions. */}
          {action === PRODUCT_ACTION && productOn && (
            // The rule is anchored under the parent's checkbox rather than at
            // the label, so the nesting reads as "these belong to that" at a
            // glance instead of as a second, narrower list.
            <div className='ml-3 space-y-0.5 border-l border-border/70 pl-2.5'>
              {PRODUCT_OPERATION_OPTIONS.map(({ value: op, label: opLabel, ready }) => (
                <label
                  key={op}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 leading-snug transition-colors hover:bg-muted/60',
                    text,
                  )}
                >
                  <Checkbox
                    className='shrink-0'
                    checked={actions.includes(op)}
                    disabled={disabled}
                    onCheckedChange={() => onChange(toggleFormAction(actions, op))}
                  />
                  <span className='flex-1'>{opLabel}</span>
                  {/* Selectable before it works, deliberately: the form says what
                      it offers, and the operation lights up when its ticket
                      ships rather than needing every definition re-edited. */}
                  {!ready && (
                    <span className='shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
                      Soon
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
